import React, { useEffect, useMemo, useState } from 'react';
import SEO from '../../components/SEO';
import PageLoader from '../../components/PageLoader';
import api from '../../utils/api';
import AdminLayout from '../../components/admin/AdminLayout';
import AdminListToolbar from '../../components/admin/AdminListToolbar';
// Force rebuild for SEO fields
import Pagination from '../../components/Pagination';
import { useSidebar } from '../../contexts/SidebarContext';
import { showSuccess, showError, showConfirm } from '../../utils/toast';
import * as LucideIcons from 'lucide-react';
import { categoryThemes, getTheme } from '../../utils/themes';
import ImageUpload from '../../components/ImageUpload';

// List of monochromatic theme keys (variations of single color)
const monochromaticThemes = [
  'blue', 'green', 'purple', 'red', 'orange', 'amber', 'teal', 'pink', 'indigo', 'cyan',
  'emerald', 'violet', 'rose', 'lime', 'sky', 'fuchsia', 'slate', 'stone', 'zinc', 'neutral',
  'yellow', 'brown', 'mint', 'coral', 'lavender', 'turquoise', 'maroon', 'navy', 'sapphire', 'forest',
  'jade', 'ruby', 'tangerine', 'gold', 'plum', 'magenta', 'cerulean', 'olive', 'burgundy', 'charcoal'
];

// List of single-shade theme keys (exact same color for all properties)
const singleShadeThemes = [
  'solidBlue', 'solidGreen', 'solidPurple', 'solidRed', 'solidOrange', 'solidAmber', 'solidTeal', 'solidPink',
  'solidIndigo', 'solidCyan', 'solidEmerald', 'solidViolet', 'solidRose', 'solidLime', 'solidSky', 'solidFuchsia',
  'solidYellow', 'solidBrown', 'solidNavy', 'solidMaroon', 'solidSlate', 'solidGray', 'solidBlack', 'solidWhite',
  'solidLavender', 'solidTurquoise', 'solidCoral', 'solidMagenta', 'solidOlive', 'solidGold', 'solidPlum',
  'solidRuby', 'solidSapphire', 'solidJade'
];

// Get multi-color themes (all themes that are not monochromatic or single-shade)
const getMultiColorThemes = () => {
  return Object.keys(categoryThemes).filter(key =>
    !monochromaticThemes.includes(key) && !singleShadeThemes.includes(key)
  );
};

const AdminCategoriesContent = () => {
  const { isCollapsed } = useSidebar();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', metaTitle: '', metaDescription: '', active: true, featured: false, icon: 'Folder', theme: 'blue', image: '', imageFile: null });
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [showThemePicker, setShowThemePicker] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('a-z');
  const [limit, setLimit] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // Comprehensive icon list for categories (200+ icons)
  const popularIcons = [
    // Business & Commerce
    'Folder', 'Building', 'Building2', 'Store', 'ShoppingBag', 'ShoppingCart', 'Package', 'Box', 'Warehouse',
    'Briefcase', 'CreditCard', 'Wallet', 'DollarSign', 'Coins', 'TrendingUp', 'BarChart', 'PieChart',
    'Receipt', 'FileText', 'File', 'Archive', 'FolderOpen', 'Database', 'FileCheck', 'FileX',

    // Food & Dining
    'UtensilsCrossed', 'Utensils', 'Coffee', 'Pizza', 'IceCream', 'Cake', 'Cookie', 'Wine',
    'ChefHat', 'CupSoda', 'Apple', 'Carrot', 'Fish', 'Drumstick', 'Beef', 'Salad',
    'Croissant', 'Donut', 'Candy', 'Cherry', 'Grape', 'Lemon', 'Orange', 'Banana',

    // Transportation
    'Car', 'CarFront', 'Truck', 'Plane', 'Ship', 'Bike', 'Train', 'Bus', 'Taxi', 'Motorcycle', 'Scooter',
    'Navigation', 'Navigation2', 'MapPin', 'Compass', 'Route', 'CarTaxiFront',

    // Home & Living
    'Home', 'House', 'DoorOpen', 'DoorClosed', 'Bed', 'Sofa', 'Lamp', 'Armchair', 'Bath', 'Shower',
    'Couch', 'Table', 'Chair', 'Fence', 'TreePine', 'Flower', 'Leaf', 'Sprout', 'Window',

    // Health & Fitness
    'Heart', 'Stethoscope', 'Activity', 'Dumbbell', 'Running', 'Swimming', 'Yoga',
    'Pill', 'Cross', 'HeartPulse', 'Syringe', 'Bandage', 'Thermometer', 'HeartHandshake', 'Crosshair',

    // Education & Learning
    'GraduationCap', 'Book', 'BookOpen', 'School', 'Library', 'Pen', 'Pencil', 'Highlighter',
    'Calculator', 'Microscope', 'Beaker', 'Flask', 'Atom', 'Brain', 'Lightbulb',
    'BookMarked', 'BookCheck', 'Notebook', 'NotebookPen',

    // Technology & Electronics
    'Laptop', 'Computer', 'Smartphone', 'Phone', 'Tablet', 'Monitor', 'Mouse', 'Keyboard',
    'Headphones', 'Speaker', 'Radio', 'Tv', 'Gamepad2', 'Cpu', 'HardDrive', 'Server',
    'Wifi', 'Bluetooth', 'Battery', 'BatteryCharging', 'Power', 'Plug', 'Scan', 'ScanLine',

    // Entertainment & Media
    'Music', 'Music2', 'Music3', 'Music4', 'Video', 'Camera', 'Film', 'Clapperboard', 'Podcast',
    'Theater', 'Ticket', 'Palette', 'Paintbrush', 'Brush', 'Image', 'Images',

    // Services & Tools
    'Wrench', 'Hammer', 'Screwdriver', 'Drill', 'Saw', 'Tool', 'Settings', 'Settings2', 'Cog',
    'Scissors', 'Ruler', 'ScrewdriverWrench', 'Paintbrush', 'Spray', 'PaintRoller',

    // Fashion & Beauty
    'Shirt', 'Sparkles', 'Droplet', 'Heart', 'Star', 'Gem', 'Diamond', 'Crown',
    'Watch', 'Glasses', 'Sun', 'Moon',

    // Family & Personal
    'Baby', 'Users', 'Users2', 'User', 'UserPlus', 'UserCheck', 'UserCircle', 'Gift', 'Cake',
    'PartyPopper', 'Balloon', 'Crown', 'Gem', 'Diamond',

    // Nature & Outdoors
    'Tree', 'Trees', 'TreePine', 'Flower', 'Flower2', 'Leaf', 'Mountain', 'MountainSnow', 'Sun', 'Moon',
    'Cloud', 'CloudRain', 'CloudSnow', 'CloudLightning', 'Droplet', 'Wind', 'Umbrella', 'Tent', 'Campfire',
    'Sunflower', 'Rose',

    // Sports & Recreation
    'Trophy', 'Medal', 'Target', 'Bowling', 'Golf', 'Tennis', 'Basketball', 'Football',
    'Volleyball', 'Baseball', 'Hockey', 'Soccer', 'Swimming', 'Skiing', 'Snowboarding',
    'TableTennis', 'Badminton',

    // Travel & Tourism
    'Plane', 'PlaneLanding', 'PlaneTakeoff', 'Ship', 'Train', 'Hotel', 'Map', 'Compass', 'Suitcase', 'Luggage',
    'Passport', 'Globe', 'MapPin', 'Navigation', 'Binoculars', 'Camera', 'Backpack',

    // Communication
    'Mail', 'MessageSquare', 'MessageCircle', 'MessageCircleReply', 'Phone', 'PhoneCall', 'PhoneIncoming',
    'PhoneOutgoing', 'Video', 'Mic', 'Headphones', 'Radio', 'Megaphone', 'Bell', 'BellRing',
    'Notification', 'Send', 'Inbox',

    // Time & Calendar
    'Calendar', 'CalendarDays', 'CalendarCheck', 'CalendarClock', 'Clock', 'Clock3', 'Clock4', 'Clock5',
    'Clock6', 'Clock7', 'Clock8', 'Clock9', 'Clock10', 'Clock11', 'Clock12', 'Timer', 'AlarmClock', 'Hourglass',

    // Symbols & Misc
    'Star', 'Heart', 'Zap', 'Shield', 'Lock', 'Key', 'Gift', 'Crown', 'Gem', 'Diamond',
    'Sparkles', 'Flame', 'Lightbulb', 'Rocket', 'Flag', 'Award', 'Badge', 'CheckCircle',
    'XCircle', 'AlertCircle', 'Info', 'HelpCircle', 'Search', 'Filter', 'Menu', 'MoreVertical',
    'ThumbsUp', 'ThumbsDown', 'Smile', 'Frown', 'Meh', 'Laugh', 'Angry', 'Happy'
  ];

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/admin/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredAndSorted = useMemo(() => {
    let list = categories;
    const s = (search || '').toLowerCase().trim();
    if (s) {
      list = list.filter((c) =>
        (c.name || '').toLowerCase().includes(s) || (c.slug || '').toLowerCase().includes(s)
      );
    }
    list = [...list].sort((a, b) => {
      const na = (a.name || '').toLowerCase();
      const nb = (b.name || '').toLowerCase();
      return sort === 'z-a' ? nb.localeCompare(na) : na.localeCompare(nb);
    });
    return list;
  }, [categories, search, sort]);

  const totalItems = filteredAndSorted.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / limit));
  const paginatedList = useMemo(
    () => filteredAndSorted.slice((currentPage - 1) * limit, currentPage * limit),
    [filteredAndSorted, currentPage, limit]
  );


  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // 1. Send JSON Data (Create or Update Category)
      const data = {
        name: formData.name,
        description: formData.description || '',
        metaTitle: formData.metaTitle || '',
        metaDescription: formData.metaDescription || '',
        active: formData.active,
        featured: formData.featured,
        icon: formData.icon,
        theme: formData.theme
      };

      let categoryId;
      let response;

      if (editingCategory) {
        response = await api.put(`/categories/${editingCategory._id}`, data);
        categoryId = editingCategory._id;
      } else {
        response = await api.post('/categories', data);
        categoryId = response.data._id;
      }

      // 2. Upload Image Separately (if present)
      if (formData.imageFile) {
        const imageForm = new FormData();
        imageForm.append('image', formData.imageFile);

        try {
          console.log(`Uploading image for category ${categoryId}...`);
          const imageResponse = await api.post(`/categories/${categoryId}/image`, imageForm, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          console.log('Image upload success:', imageResponse.data);
        } catch (imgError) {
          console.error('Image upload failed:', imgError);
          showError('Category updated but image upload failed');
        }
      }

      setShowModal(false);
      setEditingCategory(null);
      setFormData({ name: '', description: '', metaTitle: '', metaDescription: '', active: true, featured: false, icon: 'Folder', theme: 'blue', image: '', imageFile: null });
      setShowIconPicker(false);
      setShowThemePicker(false);
      fetchCategories();

      const updatedTime = response.data.updatedAt ? new Date(response.data.updatedAt).toLocaleTimeString() : 'Unknown';
      showSuccess(editingCategory
        ? `Category updated successfully! Saved at: ${updatedTime}`
        : 'Category created successfully!');
    } catch (error) {
      showError(error.response?.data?.message || 'Error saving category');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      metaTitle: category.metaTitle || '',
      metaDescription: category.metaDescription || '',
      active: category.active,
      featured: category.featured || false,
      icon: category.icon || 'Folder',
      theme: category.theme || 'blue',
      image: category.image || '',
      imageFile: null
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    showConfirm(
      'Are you sure you want to delete this category? This action cannot be undone.',
      async () => {
        try {
          await api.delete(`/categories/${id}`);
          fetchCategories();
          showSuccess('Category deleted successfully!');
        } catch (error) {
          showError(error.response?.data?.message || 'Error deleting category');
        }
      }
    );
  };


  return (
    <>
      <SEO title="Manage Categories - Admin" description="Manage categories." noindex />

      <main className={`flex-1 transition-all duration-300 ${isCollapsed ? 'ml-16' : 'ml-64'}`}>
        <div className="container-custom py-6 md:py-12">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 md:mb-8">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 dark:text-gray-50">Manage Categories</h1>
            <button
              onClick={() => {
                setEditingCategory(null);
                setFormData({ name: '', description: '', metaTitle: '', metaDescription: '', active: true, featured: false, icon: 'Folder', theme: 'blue', image: '', imageFile: null });
                setShowIconPicker(false);
                setShowThemePicker(false);
                setShowModal(true);
              }}
              className="btn-primary text-sm whitespace-nowrap"
            >
              <span className="hidden sm:inline">Add Category</span>
              <span className="sm:hidden">+ Add</span>
            </button>
          </div>

          {!loading && (
            <AdminListToolbar
              searchInput={searchInput}
              onSearchInputChange={setSearchInput}
              search={search}
              onSearch={() => { setSearch(searchInput); setCurrentPage(1); }}
              onClear={() => { setSearchInput(''); setSearch(''); setCurrentPage(1); }}
              sort={sort}
              onSortChange={(v) => { setSort(v); setCurrentPage(1); }}
              limit={limit}
              onLimitChange={(v) => { setLimit(v); setCurrentPage(1); }}
              searchPlaceholder="Search by name or slug..."
            />
          )}

          {loading ? (
            <PageLoader message="Loading categories..." />
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden lg:block card overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase w-14">Sr. No.</th>
                        <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Icon</th>
                        <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Name</th>
                        <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Slug</th>
                        <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Businesses</th>
                        <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                        <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Featured</th>
                        <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                      {paginatedList.map((category, index) => {
                        const IconComponent = LucideIcons[category.icon || 'Folder'] || LucideIcons.Folder;
                        const srNo = (currentPage - 1) * limit + index + 1;
                        return (
                          <tr key={category._id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                            <td className="px-4 md:px-6 py-4 whitespace-nowrap text-gray-600 dark:text-gray-400 text-sm">{srNo}</td>
                            <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                              <IconComponent className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                            </td>
                            <td className="px-4 md:px-6 py-4 whitespace-nowrap font-semibold text-gray-900 dark:text-gray-50">{category.name}</td>
                            <td className="px-4 md:px-6 py-4 whitespace-nowrap text-gray-500 dark:text-gray-400 text-sm">{category.slug}</td>
                            <td className="px-4 md:px-6 py-4 whitespace-nowrap text-gray-600 dark:text-gray-300 text-sm">{category.businessCount ?? 0}</td>
                            <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                              <span className={`badge text-xs ${category.active ? 'badge-success' : 'badge-error'}`}>
                                {category.active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                              <span className={`badge text-xs ${category.featured ? 'badge-warning' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}>
                                {category.featured ? '⭐ Featured' : 'Not Featured'}
                              </span>
                            </td>
                            <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                              <div className="flex gap-3">
                                <button
                                  onClick={() => handleEdit(category)}
                                  className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium transition-colors"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDelete(category._id)}
                                  className="text-error-600 dark:text-error-400 hover:text-error-700 dark:hover:text-error-300 text-sm font-medium transition-colors"
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mobile Card View */}
              <div className="lg:hidden space-y-4">
                {paginatedList.map((category, index) => {
                  const IconComponent = LucideIcons[category.icon || 'Folder'] || LucideIcons.Folder;
                  const srNo = (currentPage - 1) * limit + index + 1;
                  return (
                    <div key={category._id} className="card p-4 md:p-6">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 flex items-center gap-3">
                          <IconComponent className="w-6 h-6 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{srNo}.</span>
                              <h3 className="font-bold text-lg text-gray-900 dark:text-gray-50">{category.name}</h3>
                            </div>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">{category.slug}</p>
                            <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                              {(category.businessCount ?? 0)} business{(category.businessCount ?? 0) !== 1 ? 'es' : ''}
                            </p>
                            <div className="flex flex-wrap gap-2">
                              <span className={`badge text-xs ${category.active ? 'badge-success' : 'badge-error'}`}>
                                {category.active ? 'Active' : 'Inactive'}
                              </span>
                              <span className={`badge text-xs ${category.featured ? 'badge-warning' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}>
                                {category.featured ? '⭐ Featured' : 'Not Featured'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                        <button
                          onClick={() => handleEdit(category)}
                          className="btn-outline text-sm py-1.5 px-3 flex-1"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(category._id)}
                          className="btn-outline text-sm py-1.5 px-3 flex-1 text-error-600 dark:text-error-400 border-error-600 dark:border-error-400"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {paginatedList.length === 0 && (
                <div className="card p-12 text-center">
                  <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 text-lg">{search ? 'No categories match your search.' : 'No categories found.'}</p>
                </div>
              )}

              {totalPages >= 1 && (
                <div className="mt-6">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={totalItems}
                    itemsPerPage={limit}
                    itemsOnCurrentPage={paginatedList.length}
                    onPageChange={(p) => { setCurrentPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  />
                </div>
              )}
            </>
          )}

          {showModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4 animate-fade-in">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 md:p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto animate-scale-in">
                <h2 className="text-xl md:text-2xl font-bold mb-4 text-gray-800 dark:text-gray-50">
                  {editingCategory ? 'Edit Category' : 'Add Category'}
                </h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Icon</label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowIconPicker(!showIconPicker)}
                        className="w-full input-field flex items-center justify-between cursor-pointer"
                      >
                        <div className="flex items-center gap-2">
                          {formData.icon && (() => {
                            const IconComponent = LucideIcons[formData.icon] || LucideIcons.Folder;
                            return <IconComponent className="w-5 h-5 text-primary-600 dark:text-primary-400" />;
                          })()}
                          <span className="text-gray-700 dark:text-gray-300">{formData.icon || 'Folder'}</span>
                        </div>
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {showIconPicker && (
                        <div className="absolute z-10 mt-2 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-96 overflow-y-auto">
                          <div className="p-3 grid grid-cols-6 sm:grid-cols-8 gap-2">
                            {popularIcons.map((iconName) => {
                              const IconComponent = LucideIcons[iconName] || LucideIcons.Folder;
                              return (
                                <button
                                  key={iconName}
                                  type="button"
                                  onClick={() => {
                                    setFormData({ ...formData, icon: iconName });
                                    setShowIconPicker(false);
                                  }}
                                  className={`p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center justify-center ${formData.icon === iconName
                                    ? 'bg-primary-100 dark:bg-primary-900/30 border-2 border-primary-500 dark:border-primary-400'
                                    : 'border-2 border-transparent'
                                    }`}
                                  title={iconName}
                                >
                                  <IconComponent className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Category Image</label>
                    <p className="text-xs text-gray-500 mb-2">This image will be used as a default for businesses in this category that don't have their own images.</p>
                    <ImageUpload
                      images={formData.image ? [formData.image] : []}
                      maxImages={1}
                      onImagesChange={(newImages) => {
                        if (newImages.length > 0) {
                          setFormData({ ...formData, imageFile: newImages[0].file, image: newImages[0].url });
                        } else {
                          setFormData({ ...formData, imageFile: null, image: '' });
                        }
                      }}
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Theme</label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => {
                          setShowThemePicker(!showThemePicker);
                          setShowIconPicker(false);
                        }}
                        className="w-full input-field flex items-center justify-between cursor-pointer"
                      >
                        <div className="flex items-center gap-2">
                          <div
                            className="w-5 h-5 rounded border-2 border-gray-300 dark:border-gray-600"
                            style={{ backgroundColor: getTheme(formData.theme).colors.primary }}
                          />
                          <span className="text-gray-700 dark:text-gray-300">{getTheme(formData.theme).name}</span>
                        </div>
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {showThemePicker && (
                        <div className="absolute z-10 mt-2 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-96 overflow-y-auto">
                          {/* Single Shade Themes Section - Exact Same Color */}
                          <div className="p-3">
                            <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 px-1">
                              Single Shade Themes (33) - Same Color
                            </h4>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                              {singleShadeThemes.map((themeKey) => {
                                const theme = categoryThemes[themeKey];
                                if (!theme) return null;
                                return (
                                  <button
                                    key={themeKey}
                                    type="button"
                                    onClick={() => {
                                      setFormData({ ...formData, theme: themeKey });
                                      setShowThemePicker(false);
                                    }}
                                    className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${formData.theme === themeKey
                                      ? 'border-primary-500 dark:border-primary-400 bg-primary-50 dark:bg-primary-900/30'
                                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                                      }`}
                                    title={theme.name}
                                  >
                                    <div className="flex gap-1">
                                      <div
                                        className="w-4 h-4 rounded"
                                        style={{ backgroundColor: theme.colors.primary }}
                                      />
                                      <div
                                        className="w-4 h-4 rounded"
                                        style={{ backgroundColor: theme.colors.secondary }}
                                      />
                                      <div
                                        className="w-4 h-4 rounded"
                                        style={{ backgroundColor: theme.colors.accent }}
                                      />
                                    </div>
                                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{theme.name}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>

                          {/* Monochromatic Themes Section - Color Variations */}
                          <div className="p-3 border-t border-gray-200 dark:border-gray-700">
                            <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 px-1">
                              Color Variation Themes (40) - Different Shades
                            </h4>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                              {monochromaticThemes.map((themeKey) => {
                                const theme = categoryThemes[themeKey];
                                if (!theme) return null;
                                return (
                                  <button
                                    key={themeKey}
                                    type="button"
                                    onClick={() => {
                                      setFormData({ ...formData, theme: themeKey });
                                      setShowThemePicker(false);
                                    }}
                                    className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${formData.theme === themeKey
                                      ? 'border-primary-500 dark:border-primary-400 bg-primary-50 dark:bg-primary-900/30'
                                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                                      }`}
                                    title={theme.name}
                                  >
                                    <div className="flex gap-1">
                                      <div
                                        className="w-4 h-4 rounded"
                                        style={{ backgroundColor: theme.colors.primary }}
                                      />
                                      <div
                                        className="w-4 h-4 rounded"
                                        style={{ backgroundColor: theme.colors.secondary }}
                                      />
                                      <div
                                        className="w-4 h-4 rounded"
                                        style={{ backgroundColor: theme.colors.accent }}
                                      />
                                    </div>
                                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{theme.name}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>

                          {/* Multi-Color Themes Section */}
                          <div className="p-3 border-t border-gray-200 dark:border-gray-700">
                            <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 px-1">
                              Multi-Color Themes (16) - Mixed Colors
                            </h4>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                              {getMultiColorThemes().map((themeKey) => {
                                const theme = categoryThemes[themeKey];
                                if (!theme) return null;
                                return (
                                  <button
                                    key={themeKey}
                                    type="button"
                                    onClick={() => {
                                      setFormData({ ...formData, theme: themeKey });
                                      setShowThemePicker(false);
                                    }}
                                    className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${formData.theme === themeKey
                                      ? 'border-primary-500 dark:border-primary-400 bg-primary-50 dark:bg-primary-900/30'
                                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                                      }`}
                                    title={theme.name}
                                  >
                                    <div className="flex gap-1">
                                      <div
                                        className="w-4 h-4 rounded"
                                        style={{ backgroundColor: theme.colors.primary }}
                                      />
                                      <div
                                        className="w-4 h-4 rounded"
                                        style={{ backgroundColor: theme.colors.secondary }}
                                      />
                                      <div
                                        className="w-4 h-4 rounded"
                                        style={{ backgroundColor: theme.colors.accent }}
                                      />
                                    </div>
                                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{theme.name}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows="3"
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Meta Title (SEO)</label>
                    <input
                      type="text"
                      value={formData.metaTitle}
                      onChange={(e) => setFormData({ ...formData, metaTitle: e.target.value })}
                      placeholder="Custom page title for SEO"
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Meta Description (SEO)</label>
                    <textarea
                      value={formData.metaDescription}
                      onChange={(e) => setFormData({ ...formData, metaDescription: e.target.value })}
                      rows="2"
                      placeholder="Custom meta description for SEO"
                      className="input-field"
                    />
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <label className="ml-2 text-gray-700 dark:text-gray-300">Active</label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.featured}
                      onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <label className="ml-2 text-gray-700 dark:text-gray-300">⭐ Featured (Show on Home Page)</label>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-4 pt-4">
                    <button
                      type="submit"
                      className="btn-primary flex-1"
                    >
                      {editingCategory ? 'Update' : 'Create'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowModal(false);
                        setEditingCategory(null);
                        setShowIconPicker(false);
                        setShowThemePicker(false);
                      }}
                      className="btn-secondary flex-1"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </main>
    </>
  );
};

const AdminCategories = () => {
  return (
    <AdminLayout>
      <AdminCategoriesContent />
    </AdminLayout>
  );
};

export default AdminCategories;

